import '../config/sentry';
import * as Sentry from '@sentry/react';
import { useDispatch as useReduxDispatch } from 'react-redux';
import { rootReducer } from 'reducers';
import type { RootState } from 'reducers/types';
import {
  type Action,
  type AnyAction,
  applyMiddleware,
  compose,
  createStore,
  type Store,
} from 'redux';
import { createLogger } from 'redux-logger';
import { type ThunkDispatch, withExtraArgument } from 'redux-thunk';
import { EmbeddedRepository } from 'repositories/embedded/embedded';
import { heapAgent } from 'services/analytics/heapAgent/heapAgent';
import { apiV3 } from 'services/apiV3';
import { setStore } from 'services/apiV3/fetch';
import { lrs } from 'services/lrs/lrs';
import * as offer from 'services/offer';
import * as orders from 'services/orders';
import { payex } from 'services/payex';
import { payments } from 'services/payments/payments';
import { promotions } from 'services/promotions/promotions';
import { tracker } from 'services/tracker/tracker';
import { userLanguage } from 'services/userLanguage/userLanguage';
import { wallet } from 'services/wallet/wallet';
import {
  Authentication,
  ErrorLogger,
  Locale,
  UINotifications,
} from './middlewares';

export type StoreServices = typeof SERVICES;

// Useful to type actions
type ThunkAction<
  R, // Return type of the thunk function
  S, // state type used by getState
  E, // any "extra argument" injected into the thunk
  A extends Action, // known types of actions that can be dispatched
> = (
  dispatch: ThunkDispatch<S, E, A>,
  getState: () => S,
  extraArgument: E,
) => R;

export type PayexThunkAction<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  StoreServices,
  AnyAction
>;

type PayexThunkActionArgs = Parameters<PayexThunkAction>;

export type PayexDispatch = PayexThunkActionArgs[0];
export type PayexGetState = PayexThunkActionArgs[1];
export type PayexStoreServices = PayexThunkActionArgs[2];

export const useDispatch = () => useReduxDispatch<PayexDispatch>();

const SERVICES = {
  apiV3,
  tracker,
  wallet,
  lrs,
  userLanguage,
  offer,
  orders,
  payments,
  heapAgent,
  payex,
  promotions,
};

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  configureScopeWithState: (scope, state: RootState) => {
    const recipient = state?.ui?.fieldValues?.recipient;
    const paymentId = state?.entities?.payment?.id;
    const senderCountry = state?.ui?.fieldValues?.sender_country;
    const orderId = state?.entities?.order?.id;

    if (recipient) {
      scope.setTag('recipient', recipient);
    }

    if (paymentId) {
      scope.setTag('payment', paymentId);
    }

    if (orderId) {
      scope.setTag('order', orderId);
    }

    if (senderCountry) {
      scope.setTag('country', senderCountry);
    }
  },
});

const store: {
  store?: Store;
  get: () => Store | undefined;
  configure: (state?: Partial<RootState>) => Store;
} = {
  get() {
    return this.store;
  },
  configure() {
    let middlewares = [
      withExtraArgument(SERVICES),
      UINotifications,
      Authentication,
      Locale,
      ErrorLogger,
    ];

    const { env } = EmbeddedRepository.getProps();

    if (!['production', 'staging', 'demo'].includes(env)) {
      const logger = createLogger({ collapsed: true });
      middlewares = [...middlewares, logger];
    } else {
      middlewares = [...middlewares];
    }

    this.store = createStore(
      rootReducer,
      {},
      compose(applyMiddleware(...middlewares), sentryReduxEnhancer),
    );

    setStore(this);

    return this.store;
  },
};

export { store };
